import React from "react"
import { CookieBannerContainer } from "buoy-bottom-banner"
import UniversalNav from "buoy-universal-nav"
import Footer from "buoy-footer"
import Waypoint from "react-waypoint"
import Head from "../components/Head"
import Hero from "../components/Hero"
import HowItWorks from "../components/HowItWorks"
import Enterprise from "../components/Enterprise"
import About from "../components/About"
import Press from "../components/Press/components/Press"
import Symptoms from "../components/Symptoms"
import { BUOY_SITE_URL, IS_PRODUCTION, setBaseUrl } from "../common/constants"
import { getSubdomain } from "../common/brands"
import Source from "../common/Source"
import CovidModal from "../components/ModalOverlay/CovidModal"

const IndexPage = ({
  data,
  onLeaveNav,
  onEnterNav,
  onLeaveHero,
  onEnterHero,
  viewedCookiePolicy,
  submitViewedCookiePolicy,
  brand,
  topNavShadow,
  topNavColor,
  topNavGhostButton,
  topNavLogoSize,
  topNavTransform,
  showCovidModal,
  handleModalClose
}) => {
  const landingCopy = data.allDataJson.edges[0].node
  const products = data.allProductsJson.edges
  const site = data.site.siteMetadata

  const onCloseCookieBanner = () => {
    submitViewedCookiePolicy(true)
  }

  return (
    <div>
      <Head data={data} />
      <UniversalNav
        baseUrl={BUOY_SITE_URL}
        brand={brand}
        loggedIn={false}
        prod={IS_PRODUCTION}
        topNavShadow={topNavShadow}
        topNavColor={topNavColor}
        topNavGhostButton={topNavGhostButton}
        topNavLogoSize={topNavLogoSize}
        topNavTransform={topNavTransform}
      />
      <Waypoint onLeave={onLeaveNav} onEnter={onEnterNav} />
      <Waypoint onLeave={onLeaveHero} onEnter={onEnterHero}>
        <Hero
          {...landingCopy.hero}
          symptomCheckerPath={site.symptomCheckerPath}
        />
      </Waypoint>
      {showCovidModal && <CovidModal handleModalClose={handleModalClose}/>}
      <HowItWorks {...landingCopy.howItWorks} />
      <Press {...landingCopy.press} />
      <Enterprise {...landingCopy.enterprise} products={products} />
      <About
        {...landingCopy.about}
        symptomCheckerPath={site.symptomCheckerPath}
      />
      <Symptoms
        {...landingCopy.symptoms}
        symptomsAtoZPath={site.symptomsAtoZPath}
      />
      {viewedCookiePolicy === false && (
        <CookieBannerContainer
          prod={IS_PRODUCTION}
          onClick={onCloseCookieBanner}
        />
      )}
      <Footer baseUrl={BUOY_SITE_URL} />
    </div>
  )
}

class IndexContainer extends React.Component {
  constructor() {
    super()
    this.state = {
      pastNav: false,
      inHero: true,
      viewedCookiePolicy: false,
      brand: undefined,
      showCovidModal: true
    }

    this.restorePositionHandler = this.restorePositionHandler.bind(this)
    this.handleMassModalClose = this.handleMassModalClose.bind(this)
  }

  componentDidMount() {

    // Ensure site url is updated once we have access to window.location
    setBaseUrl()
    // Brand is kept in state and validated/updated here for SSR
    this.setBrand()

    window.onbeforeunload = () => {
      window.scrollTo(0, 0)
    }

    window.addEventListener(
      "hashchange",
      event => {
        if (event.newURL === `${BUOY_SITE_URL}/`) {
          window.scrollTo(0, 0)
        }
      },
      false
    )

    Source.getCookiePolicyViewed().then(data => {
      if (data && data.viewed_cookie_policy) {
        this.setState({ viewedCookiePolicy: data.viewed_cookie_policy })
      }
    })

    window.addEventListener("scroll", this.restorePositionHandler)
  }

  handleMassModalClose = () => {
    this.setState({ showCovidModal: false })
  }

  setBrand = () => {
    let subdomain = getSubdomain()
    if (!subdomain) {
      return Promise.resolve()
    }
    return Source.brand(subdomain)
      .then(data => {
        if (data && data.name) {
          this.setState({ brand: subdomain })
        }

        // Brand-specific redirect to avoid landing page.
        if (subdomain) {
          if (subdomain === "nyt" || subdomain === "houstonmethodist") {
            window.location.replace(`${BUOY_SITE_URL}/dashboard/`)
          } else {
            window.location.replace(`${BUOY_SITE_URL}/symptom-checker/`)
          }
        }
      })
      .catch(error => {
        console.log("Can't resolve brand: " + error)
        return Promise.resolve()
      })
  }

  restorePositionHandler() {
    // This method is needed for when a user loads the page, either by refresh
    // or forward/back buttons, and the window is not scrolled to the top.
    this.setState({
      pastNav: !(window.pageYOffset === 0),
    })
    window.removeEventListener("scroll", this.restorePositionHandler)
  }

  onLeaveNav = () => {
    this.setState({ pastNav: true })
  }

  onEnterNav = () => {
    this.setState({ pastNav: false })
  }

  onLeaveHero = () => {
    this.setState({ inHero: false })
  }

  onEnterHero = () => {
    this.setState({ inHero: true })
  }

  submitViewedCookiePolicy = () => {
    Source.updateViewedCookiePolicy(true).then(data => {
      this.setState({ viewedCookiePolicy: true })
    })
  }

  render() {
    const topNavColor = this.state.pastNav ? null : "transparent"
    const topNavLogoSize = this.state.pastNav ? "small" : "big"
    const topNavTransform = this.state.pastNav ? null : "translateY(20px)"

    return (
      <IndexPage
        {...this.props}
        {...this.state}
        brand={this.state.brand}
        onEnterNav={this.onEnterNav}
        onLeaveNav={this.onLeaveNav}
        onEnterHero={this.onEnterHero}
        onLeaveHero={this.onLeaveHero}
        viewedCookiePolicy={this.state.viewedCookiePolicy}
        submitViewedCookiePolicy={this.submitViewedCookiePolicy}
        topNavShadow={this.state.pastNav}
        topNavColor={topNavColor}
        topNavGhostButton={this.state.inHero}
        topNavLogoSize={topNavLogoSize}
        topNavTransform={topNavTransform}
        handleModalClose={this.handleMassModalClose}
      />
    )
  }
}

export const query = graphql`
  query copyDataQuery {
    site {
      siteMetadata {
        title
        description
        symptomCheckerPath
        symptomsAtoZPath
        mainImagePath
      }
    }
    allProductsJson {
      edges {
        node {
          page_url
          title
          subtitle
          copy
          color
          image {
            src
            alt
          }
          offices {
            title
            address
            email
            map
          }
          detailList {
            text
            icon
          }
        }
      }
    }
    allDataJson {
      edges {
        node {
          hero {
            headline
            image
            button
          }
          howItWorks {
            headline
            sections {
              headline
              image
              body
            }
          }
          press {
            headline
            mentions {
              publisher
              logo
              url
              height
            }
          }
          enterprise {
            headline
            label
            partners {
              name
              logo
            }
            cta {
              headline
              body
              button {
                text
                link
              }
            }
          }
          about {
            headline
            image
            body
            button
          }
          symptoms {
            headline
            button
            sections {
              image {
                src
                alt
              }
              headline
              body
              linkText
              url
            }
          }
        }
      }
    }
  }
`

export default IndexContainer



// WEBPACK FOOTER //
// ./src/pages/index.js